
import React from 'react';
import { createPortal } from 'react-dom';

interface InteractionMenuProps {
    isOpen: boolean;
    onClose: () => void;
    position: { top: number; left: number; width: number; height: number };
    onReaction: (emoji: string) => void;
    onAction: (action: 'reply' | 'copy' | 'share' | 'report' | 'delete') => void;
    isOwner: boolean;
    isAdmin?: boolean;
    type: 'message' | 'comment';
}

const EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

const InteractionMenu: React.FC<InteractionMenuProps> = ({ 
    isOpen, 
    onClose, 
    position, 
    onReaction, 
    onAction, 
    isOwner, 
    isAdmin,
    type 
}) => {
    if (!isOpen) return null;

    // --- Positioning Logic ---
    const screenHeight = window.innerHeight;
    const screenWidth = window.innerWidth;
    const menuWidth = 250; // Defined width of the menu
    const menuItemHeight = 50; // Approx height per item
    const reactionBarHeight = 60;
    const estimatedMenuHeight = reactionBarHeight + (5 * menuItemHeight); // Rough estimate

    // Determine vertical position (Top vs Bottom)
    // If element is in the bottom half, show menu ABOVE it.
    const isBottomHalf = position.top > screenHeight / 2;

    // Determine horizontal position (Center clamped to screen edges)
    let leftPos = position.left + (position.width / 2) - (menuWidth / 2);
    
    // Clamp values to ensure it stays on screen (10px padding)
    if (leftPos < 10) leftPos = 10;
    if (leftPos + menuWidth > screenWidth - 10) leftPos = screenWidth - menuWidth - 10;

    const style: React.CSSProperties = {
        position: 'absolute',
        left: `${leftPos}px`,
        width: `${menuWidth}px`,
        zIndex: 100,
    };

    if (isBottomHalf) {
        // Position above the item
        style.bottom = `${screenHeight - position.top + 10}px`;
        // Check if it goes off top
        if (parseInt(style.bottom as string) + estimatedMenuHeight > screenHeight) {
             // If it runs off the top, force it slightly lower or scroll (edge case)
             style.bottom = 'auto';
             style.top = '10px';
        }
    } else {
        // Position below the item
        style.top = `${position.top + position.height + 10}px`;
        // Check if it goes off bottom
        if (parseInt(style.top as string) + estimatedMenuHeight > screenHeight) {
            style.top = 'auto';
            style.bottom = '10px';
        }
    }

    const handleAction = (action: 'reply' | 'copy' | 'share' | 'report' | 'delete') => {
        onAction(action);
        onClose();
    };

    const [isReady, setIsReady] = React.useState(false);
    React.useEffect(() => {
        const timer = setTimeout(() => setIsReady(true), 150);
        return () => clearTimeout(timer);
    }, []);

    const handleOverlayClick = (e: React.MouseEvent) => {
        if (isReady) {
            onClose();
        }
    };

    return createPortal(
        <div 
            className="fixed inset-0 z-[9999] bg-black/60 backdrop-blur-[2px] animate-fade-in touch-none select-none" 
            style={{ WebkitUserSelect: 'none', WebkitTouchCallout: 'none' }}
            onClick={handleOverlayClick}
            onContextMenu={(e) => { e.preventDefault(); if (isReady) onClose(); }}
        >
            <div style={style} onClick={(e) => e.stopPropagation()} className="flex flex-col gap-3 animate-picker-pop-in">
                
                {/* Reaction Bar - Theme Updated */}
                <div className="bg-[var(--primary-dark-purple)] border border-white/20 rounded-full shadow-2xl p-2 flex justify-between items-center px-4">
                    {EMOJIS.map(emoji => (
                        <button
                            key={emoji}
                            onClick={() => { onReaction(emoji); onClose(); }}
                            className="text-2xl hover:scale-125 transition-transform active:scale-95 p-1"
                        >
                            {emoji}
                        </button>
                    ))}
                    <button className="text-white/50 text-xl pl-2 border-l border-white/20">
                        <i className="ph-duotone ph-plus"></i>
                    </button>
                </div>

                {/* Action Menu - Theme Updated */}
                <div className="bg-[var(--primary-dark-purple)] border border-white/20 backdrop-blur-md rounded-xl shadow-xl overflow-hidden text-white text-sm font-medium">
                    <button onClick={() => handleAction('reply')} className="w-full text-left px-4 py-3 hover:bg-white/10 flex items-center justify-between border-b border-white/10 transition-colors">
                        <span>Reply</span>
                        <i className="ph-duotone ph-arrow-u-up-left text-lg text-purple-300"></i>
                    </button>
                    
                    <button onClick={() => handleAction('copy')} className="w-full text-left px-4 py-3 hover:bg-white/10 flex items-center justify-between border-b border-white/10 transition-colors">
                        <span>Copy</span>
                        <i className="ph-duotone ph-copy text-lg text-blue-300"></i>
                    </button>

                    <button onClick={() => handleAction('share')} className="w-full text-left px-4 py-3 hover:bg-white/10 flex items-center justify-between border-b border-white/10 transition-colors">
                        <span>Share</span>
                        <i className="ph-duotone ph-share-network text-lg text-green-300"></i>
                    </button>

                    <button onClick={() => handleAction('report')} className="w-full text-left px-4 py-3 hover:bg-white/10 flex items-center justify-between border-b border-white/10 text-orange-300 transition-colors">
                        <span>Report</span>
                        <i className="ph-duotone ph-warning-circle text-lg"></i>
                    </button>

                    {(isOwner || isAdmin) && (
                        <button onClick={() => handleAction('delete')} className="w-full text-left px-4 py-3 hover:bg-red-500/20 flex items-center justify-between text-red-400 transition-colors">
                            <span>Delete</span>
                            <i className="ph-duotone ph-trash text-lg"></i>
                        </button>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default InteractionMenu;
